/**
 * "Generate from…" — WHICH of the deck's materials an AI generation reads.
 *
 * The menu that asks this used to offer three fixed rows: the deck's
 * document, its note, its flashcards. A deck now holds several documents —
 * and photo scans, recordings and videos, all of which reduce to a document
 * with an extraction — so the rows are its actual materials, enumerated from
 * the same list the pane draws its tiles from. One list, so the picker cannot
 * offer a material the pane does not show, or miss one it does.
 *
 * SINGLE-SELECT, deliberately. A generated map cites its material by index
 * into ONE array of paragraphs; concatenating two materials makes index 7
 * ambiguous, and every citation silently resolves into whichever document it
 * lands in — a highlight in the wrong file, which reads as a bug in the
 * highlight rather than in the pick. Multi-source generation needs per-
 * paragraph provenance first.
 *
 * A MAP is never a row: you do not generate from a mind map, you generate
 * one. Flashcards are a row even though they are not a material, because the
 * deck's own cards are a real thing to build a map out of and the menu has
 * always offered them.
 */

import type { DeckMaterialItem, DeckMaterialKind } from './types';

/** What the caller gets back. `flashcards` is the deck's own cards. */
export type GenerationSourcePick =
  | { kind: 'source'; sourceId: string }
  | { kind: 'note'; noteId: string }
  | { kind: 'flashcards' };

export interface GenerationSourceEntry {
  pick: GenerationSourcePick;
  /** Stable key for lists — the material's own, or `flashcards`. */
  key: string;
  label: string;
  /** Drives the row's glyph, from the one material icon table. */
  kind: DeckMaterialKind | 'flashcards';
  disabled: boolean;
  /** Why it is disabled, shown on the row. */
  hint?: string;
}

export interface GenerationSourceInput {
  /** The pane's items — the picker never enumerates anything of its own. */
  items: readonly DeckMaterialItem[];
  /** Whether the deck has cards worth building from. */
  hasCards: boolean;
  /** Pre-translated label for the flashcards row. */
  flashcardsLabel: string;
  /** Shown on a row that cannot be generated from yet. */
  unavailableHint?: string;
  /**
   * Materials known to have nothing readable in them. A source still being
   * processed is NOT in here — it is offered, and the generation's own
   * "nothing to generate from" answer covers the case where it turns out
   * empty. Offering it and failing honestly beats hiding a document the user
   * can see in the grid right above.
   */
  emptyKeys?: ReadonlySet<string>;
  /**
   * Shown on a source that is still UPLOADING ("Uploading…"). Given, a
   * pending item is offered DISABLED with this reason: there is nothing to
   * read from it yet, and the failure it would produce is the one a document
   * with no text produces, only later. Absent, a pending item is offered
   * like any other and generation's own answer covers it.
   */
  pendingHint?: string;
}

export function generationSourceEntries(
  input: GenerationSourceInput,
): GenerationSourceEntry[] {
  const entries: GenerationSourceEntry[] = [];

  for (const item of input.items) {
    if (item.ref.type === 'mindmap') continue;
    const pending = !!item.pending && input.pendingHint !== undefined;
    const empty = input.emptyKeys?.has(item.key) ?? false;
    entries.push({
      pick:
        item.ref.type === 'source'
          ? { kind: 'source', sourceId: item.ref.sourceId }
          : { kind: 'note', noteId: item.ref.noteId },
      key: item.key,
      label: item.label,
      kind: item.kind,
      disabled: pending || empty,
      hint: pending ? input.pendingHint : empty ? input.unavailableHint : undefined,
    });
  }

  entries.push({
    pick: { kind: 'flashcards' },
    key: 'flashcards',
    label: input.flashcardsLabel,
    kind: 'flashcards',
    disabled: !input.hasCards,
    hint: input.hasCards ? undefined : input.unavailableHint,
  });

  return entries;
}

/** The entry a pick came from, for a menu that shows what is selected. */
export function generationSourceKey(pick: GenerationSourcePick): string {
  switch (pick.kind) {
    case 'source':
      return `source:${pick.sourceId}`;
    case 'note':
      return `note:${pick.noteId}`;
    case 'flashcards':
      return 'flashcards';
  }
}
